import { useQuery } from "@apollo/client/react";
import { useEffect, useRef } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  H3,
  H5,
  Notification,
  P,
  Small,
} from "orcs-design-system";

import { FieldLabel, FieldStack } from "src/components/PositionFieldLayout";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import useWorkspaceDateFormats from "src/hooks/useWorkspaceDateFormats";
import { formatWorkspaceStamp } from "src/inbox/requisition/guardrailHistoryRows";

import InboxPaneLoading from "../InboxPaneLoading";
import type { InboxDetailProps } from "../registry";
import { inboxForecastTeamLookup } from "./lookup.graphql";
import {
  forecastPagePath,
  parentNameOf,
  teamTypeOf,
  type ForecastSubmissionItem,
  type ForecastTeam,
} from "./item";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(var(--inbox-enter-offset));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EnterPanel = styled(Flex)`
  --inbox-enter-offset: ${themeGet("space.r")};
  animation: ${fadeUp} 240ms cubic-bezier(0.2, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DetailTitle = styled(H3)`
  &:focus {
    outline: none;
  }
`;

const Fact = ({ label, value }: { label: string; value: string }) => {
  if (value === "") {
    return null;
  }
  return (
    <FieldStack>
      <FieldLabel>{label}</FieldLabel>
      <P m="0">{value}</P>
    </FieldStack>
  );
};

const lastSubmittedValue = (
  lastSubmittedFor: string | null,
  lastSubmittedAt: string | null,
  formats: { readable: string; readableDateTime: string }
): string => {
  if (!lastSubmittedFor) {
    return "Never";
  }
  const at = formatWorkspaceStamp(lastSubmittedAt, "datetime", formats);
  return at ? `${lastSubmittedFor} · ${at}` : lastSubmittedFor;
};

export const ForecastInboxDetail = ({
  item,
  team,
  autoFocusTitle = false,
}: {
  item: ForecastSubmissionItem;
  team: ForecastTeam | null;
  autoFocusTitle?: boolean;
}) => {
  const history = useHistory();
  const featureFlags = useWorkspaceFeatureFlags();
  const formats = useWorkspaceDateFormats();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const opened =
    item.since && moment(item.since).isValid()
      ? moment(item.since).format(formats.readable)
      : "";
  useEffect(() => {
    if (autoFocusTitle) {
      titleRef.current?.focus();
    }
  }, [autoFocusTitle]);
  return (
    <EnterPanel flexDirection="column" width="100%" height="100%" minHeight="0">
      <Box flex="1" overflow="auto" p="r">
        <Flex flexDirection="column" gap="r" width="100%">
          <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
            <Box minWidth="0">
              <Flex gap="xs" alignItems="center" flexWrap="wrap">
                <Badge variant="secondary">Forecast</Badge>
              </Flex>
              <DetailTitle
                m="0"
                mt="xs"
                tabIndex={autoFocusTitle ? -1 : undefined}
                ref={titleRef}
              >
                {item.title}
              </DetailTitle>
            </Box>
            <Badge variant="warning">Not submitted</Badge>
          </Flex>
          <Box>
            <Flex gap="l" flexWrap="wrap">
              <Fact label="Cycle" value={item.cycleKey} />
              <Fact label="Opened" value={opened} />
              <Fact
                label="Last submitted"
                value={lastSubmittedValue(
                  item.lastSubmittedFor,
                  item.lastSubmittedAt,
                  formats
                )}
              />
              <Fact label="Team type" value={teamTypeOf(team)} />
              <Fact label="Parent" value={parentNameOf(team)} />
            </Flex>
            <Divider light mt="r" />
          </Box>
          <Flex flexDirection="column" gap="xs">
            <Small uppercase weight="bold" color="greyDark">
              Why this is with you
            </Small>
            <P m="0">{item.owedAs}</P>
            <P m="0">
              The {item.cycleKey} forecast for {item.title} has not been
              submitted. Approvers and the division roll-up read submitted
              forecasts only.
            </P>
          </Flex>
        </Flex>
      </Box>
      <Box
        as="section"
        aria-labelledby="inbox-forecast-ask"
        data-testid="inbox-detail-ask"
        flexShrink={0}
        p="r"
        bg="appBackground"
        borderTop="1px solid"
        borderColor="surfaceBorder"
      >
        <Flex justifyContent="space-between" alignItems="center" gap="s" mb="s">
          <H5 id="inbox-forecast-ask" m="0">
            Your action
          </H5>
          <Small color="subText">Owner of {item.title}</Small>
        </Flex>
        <Button
          type="button"
          rightIcon={["far", "arrow-up-right-from-square"]}
          onClick={() => {
            history.push(forecastPagePath(item.teamId, featureFlags));
          }}
        >
          Open forecast
        </Button>
      </Box>
    </EnterPanel>
  );
};

type LookupQuery = {
  getGroup?: ForecastTeam | null;
};

const RegisteredForecastDetail = ({
  item,
  autoFocusTitle,
}: InboxDetailProps) => {
  if (item.kind !== "FORECAST_SUBMISSION") {
    return null;
  }
  return (
    <ForecastDetailLookup
      item={item}
      {...(autoFocusTitle ? { autoFocusTitle } : {})}
    />
  );
};

const ForecastDetailLookup = ({
  item,
  autoFocusTitle,
}: {
  item: ForecastSubmissionItem;
  autoFocusTitle?: boolean;
}) => {
  const lookup = useQuery<LookupQuery>(inboxForecastTeamLookup, {
    variables: { groupId: item.teamId },
  });
  const team =
    lookup.dataState === "complete" ? (lookup.data.getGroup ?? null) : null;
  if (lookup.loading) {
    return <InboxPaneLoading />;
  }
  if (lookup.error || lookup.dataState !== "complete" || !team) {
    return (
      <Box p="r">
        <Notification variant="error" closable={false}>
          This team could not be loaded.
        </Notification>
      </Box>
    );
  }
  return (
    <ForecastInboxDetail
      item={item}
      team={team}
      {...(autoFocusTitle ? { autoFocusTitle } : {})}
    />
  );
};

export default RegisteredForecastDetail;
